import { useState, useRef, useEffect, type CSSProperties } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLocationArrow,
  faMapMarkerAlt,
  faPlus,
  faTimes,
} from '@fortawesome/pro-regular-svg-icons';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverEvent,
  useSensors,
  useSensor,
  MouseSensor as DndMouseSensor,
  TouchSensor,
  closestCenter,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import {
  useAppDispatch,
  useAppSelector,
  spacePositionsSelector,
  availablePointsSelector,
  selectedWaypointIdsSelector,
  selectedSpacesSelector,
  toggleSelectedWaypoint,
  setSelectedWaypointIds,
  currentLocationSelector,
  setShowCurrentLocation,
  setIsTrackingLocation,
} from '../../../store';

import './WaypointSelector.scss';

interface WaypointItemProps {
  waypoint: {
    id: string;
    name: string;
    type: 'space' | 'current';
  };
  index: number;
  onRemove: (id: string) => void;
}

interface WaypointItemContentProps {
  waypoint: {
    id: string;
    name: string;
    type: 'space' | 'current';
  };
  index: number;
  containerRef?: (node: HTMLElement | null) => void;
  containerStyle?: CSSProperties;
  containerClassName?: string;
  dragHandleProps?: {
    attributes: any;
    listeners: any;
    title?: string;
  };
  onRemove?: (id: string) => void;
}

function WaypointItemContent({
  waypoint,
  index,
  containerRef,
  containerStyle,
  containerClassName = '',
  dragHandleProps,
  onRemove,
}: WaypointItemContentProps) {
  return (
    <li
      ref={containerRef}
      style={containerStyle}
      className={`waypoint-item ${containerClassName}`.trim()}
    >
      <div
        className="drag-handle"
        {...dragHandleProps?.attributes}
        {...dragHandleProps?.listeners}
        title={dragHandleProps?.title}
        aria-label={`Drag to reorder waypoint ${index + 1}: ${waypoint.name}`}
        role="button"
      />
      <span className="waypoint-number" aria-hidden="true">{index + 1}</span>
      <div className="waypoint-info">
        <FontAwesomeIcon icon={waypoint.type === 'current' ? faLocationArrow : faMapMarkerAlt} aria-hidden="true" />
        <span>{waypoint.name}</span>
      </div>
      <button
        className="remove-waypoint-btn"
        onClick={() => onRemove?.(waypoint.id)}
        disabled={!onRemove}
        title={onRemove ? 'Remove waypoint' : undefined}
        aria-label={`Remove waypoint ${index + 1}: ${waypoint.name}`}
        type="button"
      >
        <FontAwesomeIcon icon={faTimes} aria-hidden="true" />
      </button>
    </li>
  );
}

function WaypointItem({ waypoint, index, onRemove }: WaypointItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: waypoint.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : transition || 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  };

  if (isDragging) {
    return (
      <WaypointItemContent
        waypoint={waypoint}
        index={index}
        containerRef={setNodeRef}
        containerStyle={style}
        containerClassName="waypoint-placeholder"
      />
    );
  }

  return (
    <WaypointItemContent
      waypoint={waypoint}
      index={index}
      containerRef={setNodeRef}
      containerStyle={style}
      dragHandleProps={{
        attributes,
        listeners,
        title: 'Drag to reorder',
      }}
      onRemove={onRemove}
    />
  );
}

export function WaypointSelector() {
  const dispatch = useAppDispatch();

  const selectedWaypointIds = useAppSelector(selectedWaypointIdsSelector);
  const selectedSpaces = useAppSelector(selectedSpacesSelector);
  const spacePositions = useAppSelector(spacePositionsSelector);
  const availablePoints = useAppSelector(availablePointsSelector);
  const currentLocation = useAppSelector(currentLocationSelector);

  const [isAddingWaypoint, setIsAddingWaypoint] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [dragOverlayWidth, setDragOverlayWidth] = useState<number | undefined>(undefined);
  const [dragRotation, setDragRotation] = useState(0);
  const [isDropping, setIsDropping] = useState(false);
  const [pendingCurrentLocation, setPendingCurrentLocation] = useState(false);
  const waypointsListRef = useRef<HTMLOListElement>(null);
  const activeIndexRef = useRef<number>(-1);

  const selectedWaypointsInOrder = selectedWaypointIds.map((id) => {
    if (id === 'current') {
      const currentLocationPoint = availablePoints.find((p) => p.id === 'current');
      return currentLocationPoint ? { ...currentLocationPoint, space: null } : null;
    }
    const space = selectedSpaces.find((s) => s.id === id);
    const position = space ? spacePositions[space.id] : null;
    if (!space || !position) return null;
    return {
      id: space.id,
      name: space.name,
      coordinates: { lat: position.x, lng: position.y },
      type: 'space' as const,
      space,
    };
  }).filter((waypoint): waypoint is NonNullable<typeof waypoint> => waypoint !== null);

  const handleAddWaypoint = (point: typeof availablePoints[0]) => {
    dispatch(toggleSelectedWaypoint(point.id));
    setIsAddingWaypoint(false);
  };

  // Automatically add current location as waypoint when it becomes available after enabling tracking
  useEffect(() => {
    if (pendingCurrentLocation && currentLocation && !selectedWaypointIds.includes('current')) {
      const currentPoint = availablePoints.find((p) => p.id === 'current');
      if (currentPoint) {
        handleAddWaypoint(currentPoint);
        setPendingCurrentLocation(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingCurrentLocation, currentLocation, selectedWaypointIds, availablePoints]);

  const sensors = useSensors(
    useSensor(DndMouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 6,
      },
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    const id = event.active.id as string;
    setActiveId(id);
    setIsDropping(false);
    setDragRotation(0);
    activeIndexRef.current = selectedWaypointIds.indexOf(id);
    if (waypointsListRef.current) {
      setDragOverlayWidth(waypointsListRef.current.offsetWidth);
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    if (!event.over) {
      setDragRotation(0);
      return;
    }

    const currentIndex = selectedWaypointIds.indexOf(event.over.id as string);
    const originalIndex = activeIndexRef.current;

    if (currentIndex !== -1 && originalIndex !== -1) {
      const direction = currentIndex > originalIndex ? 1 : currentIndex < originalIndex ? -1 : 0;
      // Pivot up (negative rotation) when dragging down, pivot down (positive rotation) when dragging up
      setDragRotation(direction * -3);
    } else {
      setDragRotation(0);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    setIsDropping(true);
    setDragRotation(0);

    if (!over || active.id === over.id) {
      setTimeout(() => {
        setActiveId(null);
        setIsDropping(false);
      }, 200);
      return;
    }

    const oldIndex = selectedWaypointIds.indexOf(active.id as string);
    const newIndex = selectedWaypointIds.indexOf(over.id as string);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newOrder = arrayMove(selectedWaypointIds, oldIndex, newIndex);
      dispatch(setSelectedWaypointIds(newOrder));
    }

    setTimeout(() => {
      setActiveId(null);
      setIsDropping(false);
    }, 200);
  };

  const handleRemoveWaypoint = (id: string) => {
    dispatch(toggleSelectedWaypoint(id));
  };

  const activeWaypoint = activeId
    ? selectedWaypointsInOrder.find((wp) => wp.id === activeId)
    : null;

  return (
    <div className="location-input-group">
      <label htmlFor="waypoints-list">Waypoints:</label>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={selectedWaypointIds}
          strategy={verticalListSortingStrategy}
        >
          <ol
            id="waypoints-list"
            className="waypoints-list"
            ref={waypointsListRef}
            aria-label="Selected waypoints"
          >
            {selectedWaypointsInOrder.length === 0 ? (
              <li className="empty-state" aria-live="polite">
                No waypoints selected. Click "Add Waypoint" to start.
              </li>
            ) : (
              selectedWaypointsInOrder.map((waypoint, index) => (
                <WaypointItem
                  key={waypoint.id}
                  waypoint={waypoint}
                  index={index}
                  onRemove={handleRemoveWaypoint}
                />
              ))
            )}
          </ol>
        </SortableContext>
        <DragOverlay>
          {activeWaypoint && (
            <WaypointItemContent
              waypoint={activeWaypoint}
              index={selectedWaypointIds.indexOf(activeWaypoint.id)}
              containerStyle={{
                ...(dragOverlayWidth ? { width: `${dragOverlayWidth}px` } : {}),
                transform: `rotate(${dragRotation}deg)`,
                transition: isDropping ? 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease-out' : 'none',
              }}
              containerClassName={`waypoint-drag-overlay ${isDropping ? 'dropping' : ''}`.trim()}
            />
          )}
        </DragOverlay>
      </DndContext>

      <div className="location-selector">
        <button
          className={`location-btn ${isAddingWaypoint ? 'selecting' : ''}`}
          onClick={() => setIsAddingWaypoint(!isAddingWaypoint)}
          aria-expanded={isAddingWaypoint}
          aria-haspopup="listbox"
          aria-label="Add waypoint"
        >
          <FontAwesomeIcon icon={faPlus} aria-hidden="true" />
          Add Waypoint
        </button>
        {isAddingWaypoint && (
          <ul className="location-dropdown" role="listbox" aria-label="Available waypoints">
            {!selectedWaypointIds.includes('current') && (
              <li key="current">
                <button
                  role="option"
                  aria-selected="false"
                  onClick={() => {
                    if (currentLocation) {
                      const currentPoint = availablePoints.find((p) => p.id === 'current');
                      if (currentPoint) {
                        handleAddWaypoint(currentPoint);
                      }
                    } else {
                      setPendingCurrentLocation(true);
                      dispatch(setShowCurrentLocation(true));
                      dispatch(setIsTrackingLocation(true));
                    }
                  }}
                  className={`location-option ${!currentLocation ? 'pending' : ''}`}
                  aria-label={currentLocation ? 'Add current location as waypoint' : 'Enable location tracking to add current location'}
                >
                  <FontAwesomeIcon icon={faLocationArrow} aria-hidden="true" />
                  Current Location
                  {!currentLocation && <span className="pending-text"> (Enable tracking)</span>}
                </button>
              </li>
            )}
            {availablePoints
              .filter((point) => point.id !== 'current' && !selectedWaypointIds.includes(point.id))
              .map((point) => (
                <li key={point.id}>
                  <button
                    role="option"
                    aria-selected="false"
                    onClick={() => handleAddWaypoint(point)}
                    className="location-option"
                    aria-label={`Add ${point.name} as waypoint`}
                  >
                    <FontAwesomeIcon icon={faMapMarkerAlt} aria-hidden="true" />
                    {point.name}
                  </button>
                </li>
              ))}
            {availablePoints.filter((point) => point.id !== 'current' && !selectedWaypointIds.includes(point.id)).length === 0 && selectedWaypointIds.includes('current') && (
              <li className="location-option disabled">No more waypoints available</li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
}

